import type { Edge, Node } from '@xyflow/react';
import { ADD_NODE_PLACEHOLDER_ID, LAYOUT } from '../constants/constants';
import { NodeType } from '../hooks/use-sidebar-control/use-sidebar-control.types';

const makeId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;

export const recalculateLinearPosition = (nodes: Node[]) => {
  const realNodes = nodes.filter((n) => n.id !== ADD_NODE_PLACEHOLDER_ID);
  const sortedNodes = [...realNodes].sort((a, b) => a.position.x - b.position.x);
  const defaultNodeWidth = 320;
  const defaultHorizontalMargin = 50;
  let currentX = 200;

  return sortedNodes.map((node) => {
    const updatedNode = {
      ...node,
      position: { x: currentX, y: 200 },
    };
    currentX += defaultNodeWidth + defaultHorizontalMargin;
    return updatedNode;
  });
};

export function appendNodeAtEnd(params: {
  nodes: Node[];
  edges: Edge[];
  nodeType: NodeType;
  data?: Record<string, unknown>;
}): { nodes: Node[]; edges: Edge[] } {
  const { nodes, edges, nodeType, data } = params;

  if (nodeType === NodeType.Wait) {
    const hasCanvasEnrollNode = nodes.some((n) => n.type === NodeType.Enroll);
    if (!hasCanvasEnrollNode) {
      return { nodes, edges };
    }
  }

  const realNodes = nodes.filter((n) => n.id !== ADD_NODE_PLACEHOLDER_ID);
  const sorted = [...realNodes].sort((a, b) => a.position.x - b.position.x);
  const sourceNode = sorted[sorted.length - 1];

  if (!sourceNode) {
    return { nodes, edges };
  }

  const newNodeId = makeId(nodeType);
  const newNodeX = sourceNode.position.x + LAYOUT.enrollWidth + LAYOUT.gapX;
  const newNodeY = sourceNode.position.y;

  const newNode: Node = {
    id: newNodeId,
    type: nodeType,
    position: { x: newNodeX, y: newNodeY },
    data: { ...data },
  };

  const nextEdges: Edge[] = edges.filter(
    (e) => e.target !== ADD_NODE_PLACEHOLDER_ID && e.source !== ADD_NODE_PLACEHOLDER_ID
  );
  nextEdges.push({
    id: makeId('e'),
    source: sourceNode.id,
    target: newNodeId,
    type: 'smoothstep',
  });

  const nextNodes: Node[] = [...realNodes, newNode];

  return { nodes: nextNodes, edges: nextEdges };
}

export function healEdgesAfterNodeRemoval(params: {
  edges: Edge[];
  removedNodeIds: string[];
}): Edge[] {
  const { edges, removedNodeIds } = params;

  const removedIds = new Set(removedNodeIds);

  let nextEdges = edges;

  for (const removedId of removedIds) {
    const incoming = nextEdges.find((e) => e.target === removedId);
    const outgoing = nextEdges.find((e) => e.source === removedId);

    nextEdges = nextEdges.filter((e) => e.source !== removedId && e.target !== removedId);

    if (incoming && outgoing) {
      const exists = nextEdges.some(
        (e) => e.source === incoming.source && e.target === outgoing.target
      );

      if (!exists) {
        nextEdges = [
          ...nextEdges,
          {
            id: makeId('e'),
            source: incoming.source,
            target: outgoing.target,
            type: incoming.type ?? 'smoothstep',
          },
        ];
      }
    }
  }

  return nextEdges;
}

export function shouldResetToStart(nodes: Node[]): boolean {
  return nodes.length === 0;
}
